import { useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { type FC, useMemo } from "react"
import { ChartCard } from "@/components/ChartCard"
import ChartCdn from "@/components/ChartCdn/ChartCdn"
import { MetricFilter } from "@/components/MetricFilter"
import { computeChartSeriesByCdn } from "@/helpers/charts"
import { useMetricsChartProps } from "@/hooks/charts"
import { useOverallCdnStatistics } from "@/queries/statistics"
import { useWorkspaceList } from "@/queries/workspaces"
import type { MetricGenerics } from "@/types/metric"

const CdnListMetricsChart: FC = () => {
  const search = useSearch<MetricGenerics>()
  const { data: workspace } = useSuspenseQuery({ ...useWorkspaceList(), select: d => d.results[0] })
  const { data: statistics } = useSuspenseQuery({
    ...useOverallCdnStatistics({ unit: search.metric === "usage" ? "bandwidth" : "percentage" })
  })

  const { series, isGroupingLastSeries } = useMemo(() => {
    if (search.metric === "usage") {
      return computeChartSeriesByCdn<
        api.switchns.BandwidthRepartition,
        api.switchns.BandwidthRepartitionPerCdn
      >(statistics.bandwidthPerCdn ?? [], item => item.estimatedUsedBandwidth)
    }
    return computeChartSeriesByCdn<
      api.switchns.ViewersRepartition,
      api.switchns.ViewersRepartitionPerCdn
    >(statistics.viewersPerCdn ?? [], item => item.viewersCount)
  }, [statistics, search.metric])

  const { title, key, ...chartProps } = useMetricsChartProps({ isGroupingLastSeries, series })

  return (
    <ChartCard
      additionalControls={<MetricFilter withUsage={workspace?.capacityBalancingEnabled} />}
      title={title}
    >
      <ChartCdn key={key} {...chartProps} />
    </ChartCard>
  )
}

export default CdnListMetricsChart
